import { useEffect, useState } from 'react';
import { inject } from 'mobx-react';
import { Table, Row, Col } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';

// components
import withLocalization from '~/hoc/withLocalization';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';

// elements
import RequiredStar from '../../../elements/RequiredStar.js';

// assets
import Edit from '../../../assets/img/editing.svg';
import Delete from '../../../assets/img/deleting.svg';

const SkillManagement = ({ t, skillsStore, commonStore }) => {
    const [loading, setLoading] = useState(false);
    const [skills, setSkills] = useState([]);
    const [name, setName] = useState('');
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchSkills();
    }, []);

    const fetchSkills = async () => {
        setLoading(true);
        try {
            const data = await skillsStore.loadList({});
            setSkills(data || []);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            return commonStore.addNotification(t('Please enter skill name'), null, 'warning');
        }

        try {
            await skillsStore.save({
                id: editingId,
                name: name.trim(),
            }, !editingId);

            commonStore.addNotification(editingId ? t('Updated successfully') : t('Added successfully'), null, 'success');
            resetForm();
            fetchSkills();
        } catch (err) {
            commonStore.addNotification(err.message, null, 'error');
        }
    };

    const handleEdit = (entity) => {
        setEditingId(entity.id);
        setName(entity.name);
    };

    const handleDelete = async (id) => {
        try {
            await skillsStore.remove(id);
            commonStore.addNotification(t('Deleted successfully'), null, 'success');
            fetchSkills();
        } catch (err) {
            commonStore.addNotification(err.message, null, 'error');
        }
    };

    const resetForm = () => {
        setName('');
        setEditingId(null);
    };

    return (
        <Row>
            {/* HEADER */}
            <Row className="mb-3 align-items-center">
                <Col xs={9} className='d-flex'>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('Enter Name')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <RequiredStar />
                </Col>
                <Col xs={3} className="text-end">
                    {editingId ?
                        <>
                            <Button fill me2 icon_sm title={t('Save')}
                                style={{ width: "35px", height: " 35px" }}
                                onClick={handleSave}>
                                <i className="fa fa-save fa-xl" />
                            </Button>
                            <Button icon_sm title={t('Cancel')}
                                style={{ width: "35px", height: " 35px" }}
                                onClick={resetForm}>
                                <i className="fa fa-times fa-xl" />
                            </Button>
                        </>
                        :
                        <Button fill wd onClick={handleSave}>
                            <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Add new')}
                        </Button>
                    }
                </Col>
            </Row>

            {/* TABLE */}
            <Table hover responsive>
                <thead>
                    <tr>
                        <th width="10%">#</th>
                        <th>{t('Name')}</th>
                        <th width="20%">{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={3}>
                                <LoadingSpinner />
                            </td>
                        </tr>
                    ) : skills.length ? (
                        skills.map((skill, idx) => (
                            <tr key={uuid()}>
                                <td>{idx + 1}</td>
                                <td>{skill.name}</td>
                                <td>
                                    <Button icon_sm fill me2 onClick={() => handleEdit(skill)}>
                                        <img src={Edit} alt="edit" />
                                    </Button>
                                    <Button icon_sm_delete fill onClick={() => handleDelete(skill.id)}>
                                        <img src={Delete} alt="delete" />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={3} className="text-center">
                                <b>{t('No records found')}</b>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Row>
    );
};

export default inject(
    'commonStore',
    'skillsStore'
)(withLocalization(SkillManagement));
